import { ELEMENT_ID } from "@app/core/constants/element-ids.const";
import { PATHS } from "@app/core/constants/path.const";
import { Permission } from "@app/core/permission/enums/permission.enum";
import { EAppModule } from "@app/core/services/app-config.service";
import { ENavbarItemType, TNavigation } from "@app/shared/components/under-ui-header/types/navbar-item.type";

export const NAVBAR_ITEM: TNavigation[] = [
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.WORKSPACE",
    icon: "workspace",
    path: PATHS.home.path,
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.DASHBOARD",
        icon: "dashboard",
        path: `${PATHS.home.path}/${PATHS.dashboard.path}`,
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.TASKS",
        icon: "tasks",
        path: `${PATHS.home.path}/${PATHS.tasks.path}`,
        module: EAppModule.ApprovalWorkflow,
        permission: [Permission.VIEW_APPROVAL_TASKS],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.CAPTIVES",
    icon: "captives",
    path: PATHS.captives.path,
    // Standalone Captive Management module (Captive → Cell → Client). Participation is managed from
    // the captive/cell screens; a client's participations show as Participations. Gated on the
    // CaptiveManagement plugin module.
    module: EAppModule.CaptiveManagement,
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.CAPTIVE_REGISTRY",
        icon: "captives",
        path: PATHS.captives.path,
        // The registry owns every `/captives/…` URL except the prospect screens, which are their
        // own entry below.
        excludePaths: [`${PATHS.captives.path}/${PATHS.captives.prospects.path}`],
        permission: [Permission.VIEW_CAPTIVE_MANAGEMENT],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.CAPTIVE_PROSPECTS",
        icon: "captives",
        path: `${PATHS.captives.path}/${PATHS.captives.prospects.path}`,
        permission: [Permission.VIEW_CAPTIVE_MANAGEMENT],
      },
    ],
  },
  {
    type: ENavbarItemType.Link,
    label: "NAVBAR.CLIENTS",
    icon: "clients",
    path: PATHS.clients.path,
    permission: [Permission.VIEW_CLIENTS],
    elementId: ELEMENT_ID.NavClients,
  },
  {
    type: ENavbarItemType.Link,
    label: "NAVBAR.POLICIES",
    icon: "policy-outline",
    path: PATHS.policies.path,
    permission: [Permission.VIEW_POLICIES],
    elementId: ELEMENT_ID.NavPolicies,
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.CLAIMS",
    icon: "claims",
    path: PATHS.claims.path,
    module: EAppModule.Claims,
    children: [
      // One entry for the whole worklist: the former directory is the `All` scope of this page and drafts
      // are a tab on it, so three links that hit the same endpoint became one filtered list.
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.CLAIMS_LIST",
        icon: "claims",
        path: `${PATHS.claims.path}/${PATHS.claims.myClaims.path}`,
        permission: [Permission.VIEW_CLAIMS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.CATASTROPHE_EVENTS",
        icon: "catastrophe-events",
        path: `${PATHS.claims.path}/${PATHS.claims.catastropheEvents.path}`,
        permission: [Permission.VIEW_CLAIMS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.LOSS_RUN_REPORT",
        icon: "loss-run",
        path: `${PATHS.claims.path}/${PATHS.claims.lossRun.path}`,
        permission: [Permission.VIEW_LOSS_RUN_REPORT, Permission.VIEW_ALL_CLAIMS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.CLAIMS_CONFIGURATION",
        icon: "settings",
        path: `${PATHS.claims.path}/${PATHS.claims.configuration.path}`,
        permission: [Permission.VIEW_CLAIMS_CONFIGURATION],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.UNDERWRITING",
    icon: "underwriting",
    path: PATHS.quote.path,
    module: EAppModule.Quote,
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.QUOTES",
        icon: "policy-outline",
        path: PATHS.quote.path,
        permission: [Permission.VIEW_QUOTES],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.PRODUCTS",
    icon: "product-outlined",
    path: `${PATHS.product.path}`,
    elementId: ELEMENT_ID.NavProducts,
    children: [
      {
        type: ENavbarItemType.Link,
        activePath: `${PATHS.product.path}/${PATHS.product.client.path}`,
        label: "NAVBAR.PRODUCTS",
        icon: "products-child",
        path: `${PATHS.product.path}/${PATHS.product.client.path}/${PATHS.product.client.list.path}`,
        permission: [Permission.VIEW_PRODUCTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.RATING_TABLE",
        icon: "rating-table",
        path: `${PATHS.product.path}/${PATHS.product.ratingTable.path}`,
        module: EAppModule.Quote,
        permission: [Permission.VIEW_RATING_TABLES],
      },
      {
        type: ENavbarItemType.Link,
        activePath: `${PATHS.product.path}/${PATHS.product.admin.path}`,
        label: "NAVBAR.ADMIN_PRODUCTS",
        icon: "admin-products",
        path: `${PATHS.product.path}/${PATHS.product.admin.path}/${PATHS.product.admin.list.path}`,
        permission: [Permission.VIEW_ADMIN_PRODUCTS],
        elementId: ELEMENT_ID.NavProductsList,
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.COVERAGE_TYPES",
        icon: "coverage-types",
        path: `${PATHS.product.path}/${PATHS.coverageTypes.path}`,
        permission: [Permission.VIEW_COVERAGE_TYPES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.ATTRIBUTES",
        icon: "attributes",
        path: `${PATHS.product.path}/${PATHS.customFields.path}`,
        permission: [Permission.VIEW_CUSTOM_FIELDS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.ENDORSEMENTS",
        icon: "endorsements",
        path: `${PATHS.product.path}/${PATHS.product.endorsements.path}`,
        permission: [Permission.VIEW_ENDORSEMENT_FORMS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.RISK_TYPES",
        icon: "risk-types",
        path: `${PATHS.product.path}/${PATHS.product.riskTypes.path}`,
        permission: [Permission.VIEW_RISK_TYPES],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.BILLING",
    path: PATHS.billing.path,
    icon: "billing",
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.INVOICES",
        icon: "invoices",
        path: `${PATHS.billing.path}/${PATHS.billing.invoices.path}`,
        permission: [Permission.VIEW_INVOICES],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.LOSS_RUN_INTELLIGENCE",
    icon: "loss-run-intelligence",
    path: PATHS.lossRunIntelligence.path,
    // Core code, but switched per environment like a plugin: hidden unless Modules:Enabled lists LossRun.
    module: EAppModule.LossRun,
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.LOSS_RUN",
        icon: "loss-run",
        path: `${PATHS.lossRunIntelligence.path}/${PATHS.lossRunIntelligence.lossRun.path}`,
        permission: [Permission.VIEW_LOSS_RUN],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.LOSS_RUN_TEMPLATES",
        icon: "loss-run-templates",
        path: `${PATHS.lossRunIntelligence.path}/${PATHS.lossRunIntelligence.lossRunTemplates.path}`,
        permission: [Permission.VIEW_LOSS_RUN_TEMPLATES],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.GL",
    icon: "general-ledger",
    path: PATHS.gl.path,
    // Hidden for tenants without the GeneralLedger plugin module (ROR-4522 / ROR-4530).
    module: EAppModule.GeneralLedger,
    // TODO: restore permission: Permission.VIEW_GL_ACCOUNTS once GL permissions are seeded in the DB
    children: [
      { type: ENavbarItemType.Header, label: "NAVBAR.GL_GROUP_ACCOUNTING_SETUP" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_ACCOUNTS",
        icon: "accounts",
        path: `${PATHS.gl.path}/${PATHS.gl.accounts.path}`,
        permission: [Permission.VIEW_GL_ACCOUNTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_BOOKS",
        icon: "books",
        path: `${PATHS.gl.path}/${PATHS.gl.books.path}`,
        permission: [Permission.VIEW_GL_BOOKS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_DEPARTMENTS",
        icon: "departments",
        path: `${PATHS.gl.path}/${PATHS.gl.departments.path}`,
        permission: [Permission.VIEW_GL_DEPARTMENTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_POSTING_ENGINE",
        icon: "posting-engine",
        path: `${PATHS.gl.path}/${PATHS.gl.postingEngine.path}`,
        permission: [Permission.ADMIN_GL_ACCOUNTS],
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.GL_GROUP_JOURNALS" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_JOURNAL_ENTRIES",
        icon: "journal-entries",
        path: `${PATHS.gl.path}/${PATHS.gl.journalEntries.path}`,
        permission: [Permission.VIEW_GL_ACCOUNTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_RECURRING_JOURNALS",
        icon: "recurring-journals",
        path: `${PATHS.gl.path}/${PATHS.gl.recurringJournals.path}`,
        permission: [Permission.VIEW_GL_ACCOUNTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_IMPORT_TEMPLATES",
        icon: "loss-run-templates",
        path: `${PATHS.gl.path}/${PATHS.gl.importTemplates.path}`,
        permission: [Permission.VIEW_GL_IMPORT_TEMPLATES],
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.GL_GROUP_FINANCIAL_REPORTS" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_TRIAL_BALANCE",
        icon: "trial-balance",
        path: `${PATHS.gl.path}/${PATHS.gl.trialBalance.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_SUBLEDGER_RECONCILIATION",
        icon: "subledger-reconciliation",
        path: `${PATHS.gl.path}/${PATHS.gl.subledgerReconciliation.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_BALANCE_SHEET",
        icon: "balance-sheet",
        path: `${PATHS.gl.path}/${PATHS.gl.balanceSheet.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_INCOME_STATEMENT",
        icon: "income-statement",
        path: `${PATHS.gl.path}/${PATHS.gl.incomeStatement.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "GL.EXPENSE_STATEMENT.TITLE",
        icon: "expense-statement",
        path: `${PATHS.gl.path}/${PATHS.gl.expenseStatement.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_CASH_FLOW",
        icon: "cash-flow",
        path: `${PATHS.gl.path}/${PATHS.gl.cashFlow.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_DETAIL_REPORT",
        icon: "gl-detail",
        path: `${PATHS.gl.path}/${PATHS.gl.glDetail.path}`,
        permission: [Permission.VIEW_GL_REPORTS],
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.GL_GROUP_RECEIVABLES_PAYABLES" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_AR",
        icon: "accounts-receivable",
        path: `${PATHS.gl.path}/${PATHS.gl.ar.path}/${PATHS.gl.ar.invoices.path}`,
        permission: [Permission.MANAGE_AR_INVOICES],
      },
      {
        type: ENavbarItemType.Link,
        label: "BREADCRUMBS.GL_MODULE.CASH_RECEIPTS",
        icon: "cash-transactions",
        path: `${PATHS.gl.path}/${PATHS.gl.ar.path}/${PATHS.gl.ar.receipts.path}`,
        permission: [Permission.MANAGE_AR_RECEIPTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_AP",
        icon: "accounts-payable",
        path: `${PATHS.gl.path}/${PATHS.gl.ap.path}/${PATHS.gl.ap.bills.path}`,
        permission: [Permission.MANAGE_AP_BILLS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_AR_CUSTOMERS",
        icon: "ar-customers",
        path: `${PATHS.gl.path}/${PATHS.gl.ar.path}/${PATHS.gl.ar.customers.path}`,
        permission: [Permission.MANAGE_AR_INVOICES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_VENDORS",
        icon: "vendors",
        path: `${PATHS.gl.path}/${PATHS.gl.ap.path}/${PATHS.gl.ap.vendors.path}`,
        permission: [Permission.MANAGE_AP_BILLS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_AR_AGING",
        icon: "ar-aging",
        path: `${PATHS.gl.path}/${PATHS.gl.arAging.path}`,
        permission: [Permission.MANAGE_AR_INVOICES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_AP_AGING",
        icon: "ap-aging",
        path: `${PATHS.gl.path}/${PATHS.gl.apAging.path}`,
        permission: [Permission.MANAGE_AP_BILLS],
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.GL_GROUP_BANKING" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_BANK_RECONCILIATION",
        icon: "bank-reconciliation",
        path: `${PATHS.gl.path}/${PATHS.gl.bankReconciliation.path}`,
        permission: [Permission.VIEW_BANK_RECON],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.GL_CASH_TRANSACTIONS",
        icon: "cash-transactions",
        path: `${PATHS.gl.path}/${PATHS.gl.cashTransactions.path}`,
        permission: [Permission.VIEW_BANK_RECON],
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.REPORTING",
    icon: "file-chart-column",
    path: PATHS.reporting.path,
    // Hidden for tenants without the Reporting plugin module, and for users without
    // the view grant (ROR-5534).
    module: EAppModule.Reporting,
    permission: [Permission.VIEW_REPORTING],
    children: [
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.SAVED_REPORTS",
        icon: "file-chart-column",
        path: PATHS.reporting.path,
      },
    ],
  },
  {
    type: ENavbarItemType.Group,
    label: "NAVBAR.SETTINGS",
    icon: "settings",
    path: PATHS.settings.path,
    children: [
      { type: ENavbarItemType.Header, label: "NAVBAR.SETTINGS_GROUP_ADMINISTRATION" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.USERS",
        icon: "users",
        path: `${PATHS.settings.path}/${PATHS.settings.users.path}`,
        permission: [Permission.VIEW_USERS_AND_ACCESS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.ROLES",
        icon: "roles",
        path: `${PATHS.settings.path}/${PATHS.settings.roles.path}`,
        permission: [Permission.VIEW_ROLES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.ORGANIZATIONS",
        icon: "organizations",
        path: `${PATHS.settings.path}/${PATHS.settings.organisations.path}`,
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.SETTINGS_GROUP_CONFIGURATION" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.EMAIL_TEMPLATES",
        icon: "email-logs",
        path: `${PATHS.settings.path}/${PATHS.settings.emailTemplates.path}`,
        permission: [Permission.VIEW_EMAIL_TEMPLATES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.TERRITORIES_TAXES",
        icon: "territories-and-taxes",
        path: `${PATHS.settings.path}/${PATHS.settings.territoriesAndTaxes.path}`,
        permission: [Permission.VIEW_TAXES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.BANK_ACCOUNTS",
        icon: "accounts",
        path: `${PATHS.settings.path}/${PATHS.settings.bankAccounts.path}`,
        permission: [Permission.VIEW_BANK_ACCOUNTS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.TEMPLATE_CENTRE",
        icon: "document-template-center",
        path: `${PATHS.settings.path}/${PATHS.settings.templateCenter.path}`,
        permission: [Permission.VIEW_TEMPLATES],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.TAGS_LIBRARY",
        icon: "tags-library",
        path: `${PATHS.settings.path}/${PATHS.settings.tagsLibrary.path}`,
        permission: [Permission.VIEW_TAGS],
      },

      { type: ENavbarItemType.Header, label: "NAVBAR.SETTINGS_GROUP_SYSTEM_MANAGEMENT" },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.NOTIFICATIONS",
        icon: "notifications",
        path: `${PATHS.settings.path}/${PATHS.settings.notifications.path}`,
        permission: [Permission.VIEW_NOTIFICATION_SETTINGS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.SECURITY",
        icon: "coverage-types",
        path: `${PATHS.settings.path}/${PATHS.settings.security.path}`,
        permission: [Permission.VIEW_PASSWORD_POLICY],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.EMAIL_LOGS",
        icon: "email-logs",
        path: `${PATHS.settings.path}/${PATHS.settings.emailLogs.path}`,
        permission: [Permission.VIEW_EMAIL_LOGS],
      },
      {
        type: ENavbarItemType.Link,
        label: "NAVBAR.WORKFLOWS",
        icon: "workflows",
        path: `${PATHS.settings.path}/${PATHS.settings.workflowRules.path}`,
        permission: [Permission.VIEW_APPROVAL_WORKFLOWS],
      },
    ],
  },
];
